import {GameObjectsTags} from '../../../ClickerGame.constants';
import {DI_TOKENS} from '@/di/di.tokens';
import {FULL_BATTERY_LEVEL} from '@/stores/ClickerStateStore/ClickerStateStore.constants';
import {Texture} from 'pixi.js';

import {injected} from 'brandi';

import {BaseComponent, IGameObject} from '@specials/gcs-engine';

import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {GameObjectUtils} from '@/utils/GameObjectUtils';

import {ClickerAudioPlayer, ClickerSoundsBundle} from '../../../ClickerAudioPlayer';
import {ClickerGameAssetsLoader} from '../../../ClickerGameAssetsLoader';
import {ISpaceshipDataComponent} from './SpaceshipDataComponent';
import {SpaceshipViewComponent} from './SpaceshipViewComponent';

export enum KotletkaState {
  Happy = 'Happy',
  Sad = 'Sad',
  TransitionToSad = 'TransitionToSad',
  TransitionToHappy = 'TransitionToHappy',
  TransitionToSleepy = 'TransitionToSleepy',
  TransitionSleepyToHappy = 'TransitionSleepyToHappy',
  Sleepy = 'Sleepy',
}

// https://wiki.tcsbank.ru/pages/viewpage.action?pageId=4076220144
// анимация на какие-либо действия
enum ReactionAnimationType {
  HappyClick = 'HappyClick',
  SadClick = 'SadClick',
  IntensiveClick = 'IntensiveClick',
  DropAsteroid = 'DropAsteroid',
  DropComet = 'DropComet',
  ComboX100 = 'ComboX100',
  DailyCompleted = 'DailyCompleted',
}

export class SpaceshipAnimationComponent extends BaseComponent {
  private viewComponent?: SpaceshipViewComponent;
  private framesMap: {
    kotletkaHappy: Texture[];
    kotletkaClick: Texture[];
    kotletkaSadClick: Texture[];
    kotletkaIntensiveClick: Texture[];
    kotletkaSad: Texture[];
    kotletkaTransitionToSad: Texture[];
    kotletkaTransitionToHappy: Texture[];
    kotletkaCrystal: Texture[];
    kotletkaTransitionToSleepy: Texture[];
    kotletkaSleepy: Texture[];
    kotletkaTransitionSleepyToHappy: Texture[];
    kotletkaFireflies: Texture[];
    kotletkaShashlyk: Texture[];
    kotletkaDailyGame: Texture[];
  };

  // фоновая анимация
  public kotletkaCurrentState: KotletkaState;
  public reactionAnimation: ReactionAnimationType | null;
  private dataComponent?: ISpaceshipDataComponent;

  constructor(
    private readonly assetsLoader: ClickerGameAssetsLoader,
    private readonly audioPlayer: ClickerAudioPlayer,
  ) {
    super();

    const bundle = this.assetsLoader.bundle;

    this.framesMap = {
      kotletkaHappy: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaHappy),
      kotletkaClick: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaClick),
      kotletkaSadClick: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaSadClick),
      kotletkaIntensiveClick: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaIntensiveClick),
      kotletkaSad: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaSad),
      kotletkaTransitionToSad: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaTransitionToSad),
      kotletkaTransitionToHappy: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaTransitionToHappy),
      kotletkaCrystal: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaCrystal),
      kotletkaTransitionToSleepy: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaTransitionToSleepy),
      kotletkaSleepy: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaSleepy),
      kotletkaTransitionSleepyToHappy: GameObjectUtils.createFramesFromSpriteSheet(
        bundle.kotletkaTransitionSleepyToHappy,
      ),
      kotletkaFireflies: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaFireflies),
      kotletkaShashlyk: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaShashlyk),
      kotletkaDailyGame: GameObjectUtils.createFramesFromSpriteSheet(bundle.kotletkaDailyGame),
    };

    this.reactionAnimation = null;
  }

  public onAddToGameObject(go: IGameObject): void {
    this.viewComponent = go.getComponent(SpaceshipViewComponent);
    this.dataComponent = go.getComponentByTag<ISpaceshipDataComponent>(GameObjectsTags.SpaceshipDataComponent);
  }

  public playSoundClickByState = (): void => {
    const state = this.kotletkaCurrentState;

    if (this.isHappyState(state)) {
      this.audioPlayer.playClickSound(ClickerSoundsBundle.KotletkaTapHappy);
    } else if (this.isSadState(state)) {
      this.audioPlayer.playClickSound(ClickerSoundsBundle.KotletkaTapSad);
    } else if (this.isSleepyState(state)) {
      this.audioPlayer.playClickSound(ClickerSoundsBundle.KotletkaTapHappy);
    }
  };

  public playHappySoundClick = (): void => {
    this.audioPlayer.playClickSound(ClickerSoundsBundle.KotletkaTapHappy);
  };

  public playDropCometaAnimation(): void {
    const state = this.kotletkaCurrentState;

    if (this.isSadState(state) || this.reactionAnimation !== null) return;

    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka) return;

    this.reactionAnimation = ReactionAnimationType.DropComet;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaCrystal;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  public playDailyCompleted(): void {
    const state = this.kotletkaCurrentState;

    if (this.isSadState(state) || this.reactionAnimation !== null) return;

    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka) return;

    this.reactionAnimation = ReactionAnimationType.DailyCompleted;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaDailyGame;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  public forcePlayTransitionToSleepy = (): void => {
    const state = this.kotletkaCurrentState;

    if (
      this.isSadState(state) ||
      this.kotletkaCurrentState === KotletkaState.Sleepy ||
      this.kotletkaCurrentState === KotletkaState.TransitionSleepyToHappy
    )
      return;

    this.kotletkaCurrentState = KotletkaState.TransitionToSleepy;
    this.playCurrentAnimation();
  };

  public playFireflies(): void {
    if (!this.isHappyState(this.kotletkaCurrentState)) return;

    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka || this.reactionAnimation !== null) return;

    this.reactionAnimation = ReactionAnimationType.DropAsteroid;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaFireflies;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  public playShashlyk(): void {
    if (!this.isHappyState(this.kotletkaCurrentState)) return;

    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka || this.reactionAnimation !== null) return;

    this.reactionAnimation = ReactionAnimationType.ComboX100;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaShashlyk;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  public playHappyClickAnimation(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka || this.reactionAnimation !== null) return;

    this.reactionAnimation = ReactionAnimationType.HappyClick;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaClick;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  public playSadClickAnimation(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka || this.reactionAnimation !== null) return;

    this.reactionAnimation = ReactionAnimationType.SadClick;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaSadClick;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  public playIntensiveClickAnimation(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka || this.reactionAnimation !== null) return;

    this.reactionAnimation = ReactionAnimationType.IntensiveClick;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaIntensiveClick;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.5;
    kotletka.gotoAndPlay(0);
    kotletka.onComplete = () => {
      this.reactionAnimation = null;
      this.playCurrentAnimation();
    };
  }

  private playHappyAnimation(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka || this.isSadState(this.kotletkaCurrentState)) return;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaHappy;
    kotletka.loop = true;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
  }

  private playSadAnimation(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka) return;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaSad;
    kotletka.loop = true;
    kotletka.animationSpeed = 0.35;
    kotletka.gotoAndPlay(0);
  }

  private playTransitionToSad(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka) return;

    this.reactionAnimation = null;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaTransitionToSad;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.onComplete = this.nextState;

    kotletka.gotoAndPlay(0);
  }

  private playTransitionToHappy(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (!kotletka) return;

    this.reactionAnimation = null;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaTransitionToHappy;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.onComplete = this.nextState;

    kotletka.gotoAndPlay(0);
  }

  private playTransitionToSleepy(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (
      !kotletka ||
      this.kotletkaCurrentState === KotletkaState.Sleepy ||
      this.kotletkaCurrentState === KotletkaState.TransitionSleepyToHappy
    )
      return;

    this.reactionAnimation = null;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaTransitionToSleepy;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.onComplete = this.nextState;

    kotletka.gotoAndPlay(0);
  }

  private playSleepy(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (
      !kotletka ||
      this.kotletkaCurrentState === KotletkaState.TransitionToSleepy ||
      this.kotletkaCurrentState === KotletkaState.TransitionSleepyToHappy
    )
      return;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaSleepy;
    kotletka.loop = true;
    kotletka.animationSpeed = 0.35;

    kotletka.gotoAndPlay(0);
  }

  private playTransitionSleepyToHappy(): void {
    const kotletka = this.viewComponent?.kotletka;

    if (
      !kotletka ||
      this.kotletkaCurrentState === KotletkaState.TransitionToSleepy ||
      this.kotletkaCurrentState === KotletkaState.Sleepy
    )
      return;

    this.reactionAnimation = null;

    kotletka.stop();
    kotletka.textures = this.framesMap.kotletkaTransitionSleepyToHappy;
    kotletka.loop = false;
    kotletka.animationSpeed = 0.35;
    kotletka.onComplete = this.nextState;

    kotletka.gotoAndPlay(0);
  }

  public getKotletkaStateByChargeLevel(): KotletkaState {
    return ClickerStateStore.isLowBatteryLevel(this.dataComponent?.batteryChargeLevel || FULL_BATTERY_LEVEL)
      ? KotletkaState.Sad
      : KotletkaState.Happy;
  }

  public playCurrentAnimation(): void {
    const state = this.kotletkaCurrentState;

    switch (state) {
      case KotletkaState.Happy:
        this.playHappyAnimation();
        break;

      case KotletkaState.TransitionToSad:
        this.playTransitionToSad();
        break;

      case KotletkaState.Sad:
        this.playSadAnimation();
        break;

      case KotletkaState.TransitionToHappy:
        this.playTransitionToHappy();
        break;

      case KotletkaState.TransitionToSleepy:
        this.playTransitionToSleepy();
        break;

      case KotletkaState.Sleepy:
        this.playSleepy();
        break;

      case KotletkaState.TransitionSleepyToHappy:
        this.playTransitionSleepyToHappy();
        break;
    }
  }

  public nextState = (): void => {
    const state = this.kotletkaCurrentState;

    if (state === KotletkaState.Happy) {
      this.kotletkaCurrentState = KotletkaState.TransitionToSad;
    } else if (state === KotletkaState.TransitionToSad) {
      this.kotletkaCurrentState = KotletkaState.Sad;
    } else if (state === KotletkaState.Sad) {
      this.kotletkaCurrentState = KotletkaState.TransitionToHappy;
    } else if (state === KotletkaState.TransitionToHappy) {
      this.kotletkaCurrentState = KotletkaState.Happy;
    } else if (state === KotletkaState.TransitionToSleepy) {
      this.kotletkaCurrentState = KotletkaState.Sleepy;
    } else if (state === KotletkaState.Sleepy) {
      this.kotletkaCurrentState = KotletkaState.TransitionSleepyToHappy;
    } else if (state === KotletkaState.TransitionSleepyToHappy) {
      this.kotletkaCurrentState = KotletkaState.Happy;
    }

    this.playCurrentAnimation();
  };

  public isHappyState(state: KotletkaState): boolean {
    return state === KotletkaState.Happy;
  }

  public isSadState(state: KotletkaState): boolean {
    return state === KotletkaState.Sad;
  }

  public isTransitionState(state: KotletkaState): boolean {
    return (
      state === KotletkaState.TransitionToSad ||
      state === KotletkaState.TransitionToHappy ||
      state === KotletkaState.TransitionToSleepy ||
      state === KotletkaState.TransitionSleepyToHappy
    );
  }

  public isSleepyState(state: KotletkaState): boolean {
    return state === KotletkaState.Sleepy;
  }
}

injected(SpaceshipAnimationComponent, DI_TOKENS.clickerGameAssetsLoader, DI_TOKENS.clickerGameAudioPlayer);
